/* eslint-disable no-prototype-builtins */
import React from 'react';
import PropTypes from 'prop-types';
import { UX2 } from '@wsb/guac-widget-core';
import GridLayoutRow from './GridLayoutRow';
import ErrorMessage from '../ErrorMessage';
import Fetching from '../Fetching';
import dataAids from '../../../constants/dataAids';
import cardTypes from '../../../constants/commerceCardTypes';
import { mapContextToProps } from '../BaseContainerContext';

const gridProps = {
  xs: 2,
  sm: 12,
  spacingXs: 'xsmall',
  spacingSm: 'small',
  spacingMd: 'medium'
};

export class GridLayout extends React.Component {
  get showError() {
    const { willUseSampleData, useStaticData, error, configError, provisionHasError } = this.props;
    if (willUseSampleData || useStaticData) return false;
    if (provisionHasError) return true;

    return (
      (configError && configError.hasOwnProperty('status')) ||
      (error && error.hasOwnProperty('status'))
    );
  }

  /* eslint-disable complexity */
  renderProductData(translate) {
    const {
      products,
      willUseSampleData,
      productDataWasLoaded,
      imageOverlayOpacity,
      showProductPrice,
      showActionLink,
      actionLinkText,
      category,
      renderMode,
      shopWidgetPageId,
      shopWidgetRoute
    } = this.props;

    const {
      Component: {
        Grid,
        Grid: { Cell }
      },
      Element: { Block, Text }
    } = UX2;

    const noProductStyles = {
      paddingTop: 'medium',
      textAlign: 'center'
    };

    if (this.showError) {
      return <ErrorMessage message={ translate('products.error_fetching_product_data') } />;
    }
    if (!willUseSampleData && !productDataWasLoaded) {
      return null;
    }

    const sharedRowProps = {
      imageOverlayOpacity,
      showProductPrice,
      showActionLink,
      actionLinkText,
      category,
      renderMode,
      shopWidgetPageId,
      shopWidgetRoute,
      products,
      gridSize: { xs: 1, sm: 1 },
      cardType: cardTypes.MAJOR
    };

    switch (products.length) {
      case 0:
        return (
          <Block style={ noProductStyles }>
            <Text
              children={ translate('featured_products.new_products_coming_soon') }
              data-aid={ dataAids.PG_NO_PRODUCTS_MESSAGE_RENDERED }
            />
          </Block>
        );
      case 1:
        return (
          <Grid { ...gridProps }>
            <Cell xs={ 2 } sm={ 8 } pushSm={ 2 }>
              <GridLayoutRow { ...sharedRowProps } />
            </Cell>
          </Grid>
        );
      case 2:
        return (
          <Grid { ...gridProps }>
            <Cell xs={ 2 } sm={ 12 }>
              <GridLayoutRow { ...sharedRowProps } gridSize={{ xs: 1, sm: 2 }} />
            </Cell>
          </Grid>
        );
      case 3:
        return (
          <Grid { ...gridProps }>
            <Cell xs={ 2 } sm={ 8 }>
              <GridLayoutRow { ...sharedRowProps } products={ products.slice(0, 1) } />
            </Cell>
            <Cell xs={ 2 } sm={ 4 }>
              <GridLayoutRow
                { ...sharedRowProps }
                products={ products.slice(1, 3) }
                gridSize={{ xs: 2, sm: 1 }}
              />
            </Cell>
          </Grid>
        );
      case 4:
        return (
          <Grid { ...gridProps }>
            <Cell xs={ 2 } sm={ 9 }>
              <GridLayoutRow { ...sharedRowProps } products={ products.slice(0, 1) } />
            </Cell>
            <Cell xs={ 2 } sm={ 3 }>
              <GridLayoutRow
                { ...sharedRowProps }
                products={ products.slice(1, 4) }
                gridSize={{ xs: 2, sm: 1 }}
              />
            </Cell>
          </Grid>
        );
      case 5:
        return (
          <Grid { ...gridProps }>
            <Cell xs={ 2 } sm={ 6 }>
              <GridLayoutRow { ...sharedRowProps } products={ products.slice(0, 1) } />
            </Cell>
            <Cell xs={ 2 } sm={ 6 }>
              <GridLayoutRow
                { ...sharedRowProps }
                products={ products.slice(1, 5) }
                gridSize={{ xs: 2, sm: 2 }}
              />
            </Cell>
          </Grid>
        );
      case 7:
        return (
          <Grid { ...gridProps }>
            <Cell xs={ 2 } sm={ 8 }>
              <GridLayoutRow { ...sharedRowProps } products={ products.slice(0, 1) } />
            </Cell>
            <Cell xs={ 2 } sm={ 4 }>
              <GridLayoutRow
                { ...sharedRowProps }
                products={ products.slice(1, 3) }
                gridSize={{ xs: 2, sm: 1 }}
              />
            </Cell>
            <Cell xs={ 2 } sm={ 12 }>
              <GridLayoutRow
                { ...sharedRowProps }
                products={ products.slice(3, 7) }
                gridSize={{ xs: 2, sm: 2 }}
                cardType={ cardTypes.MEDIUM }
              />
            </Cell>
          </Grid>
        );
      case 10:
        return (
          <Grid { ...gridProps }>
            <Cell xs={ 2 } sm={ 8 }>
              <GridLayoutRow { ...sharedRowProps } products={ products.slice(0, 1) } />
            </Cell>
            <Cell xs={ 2 } sm={ 4 }>
              <GridLayoutRow
                { ...sharedRowProps }
                products={ products.slice(1, 3) }
                gridSize={{ xs: 2, sm: 1 }}
              />
            </Cell>
            <Cell xs={ 2 } sm={ 12 }>
              <GridLayoutRow
                { ...sharedRowProps }
                products={ products.slice(3, 7) }
                gridSize={{ xs: 2, sm: 2 }}
                cardType={ cardTypes.MEDIUM }
              />
            </Cell>
            <Cell xs={ 2 } sm={ 12 }>
              <GridLayoutRow
                { ...sharedRowProps }
                products={ products.slice(7, 10) }
                gridSize={{ xs: 2, sm: 3 }}
              />
            </Cell>
          </Grid>
        );
      default:
        // 6, 8, 9, 11, 12
        return (
          <Grid { ...gridProps }>
            <Cell xs={ 2 } sm={ 8 }>
              <GridLayoutRow { ...sharedRowProps } products={ products.slice(0, 1) } />
            </Cell>
            <Cell xs={ 2 } sm={ 4 }>
              <GridLayoutRow
                { ...sharedRowProps }
                products={ products.slice(1, 3) }
                gridSize={{ xs: 2, sm: 1 }}
              />
            </Cell>
            <Cell xs={ 2 } sm={ 12 }>
              <GridLayoutRow
                { ...sharedRowProps }
                products={ products.slice(3, 12) }
                gridSize={{ xs: 2, sm: 3 }}
              />
            </Cell>
          </Grid>
        );
    }
  }

  render() {
    const { fetching, willUseSampleData, translate } = this.props;
    const productData = this.renderProductData(translate);
    const { Block } = UX2.Element;

    return (
      <Block>
        <Block data-route=''>{ productData }</Block>
        <Fetching fetching={ fetching } willUseSampleData={ willUseSampleData } />
      </Block>
    );
  }
}

GridLayout.propTypes = {
  products: PropTypes.array,
  imageOverlayOpacity: PropTypes.number,
  showProductPrice: PropTypes.bool,
  showActionLink: PropTypes.bool,
  actionLinkText: PropTypes.string,
  category: PropTypes.string,
  renderMode: PropTypes.string,
  shopWidgetPageId: PropTypes.any,
  shopWidgetRoute: PropTypes.any,
  willUseSampleData: PropTypes.bool,
  useStaticData: PropTypes.bool,
  provisionHasError: PropTypes.bool,
  error: PropTypes.object,
  configError: PropTypes.object,
  productDataWasLoaded: PropTypes.bool,
  fetching: PropTypes.bool,
  translate: PropTypes.func
};

GridLayout.defaultProps = {
  products: [],
  configError: null,
  willUseSampleData: false,
  provisionHasError: false,
  useStaticData: false,
  translate: () => {}
};

export default mapContextToProps(GridLayout, context => ({
  translate: context.translate
}));
